import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { AdminService } from '../../../core/services/admin.service';
import { StaffDto } from '../../../core/models/admin.models';
import {
  LucideAngularModule,
  UserPlus,
  Search,
  RefreshCw,
  X,
  Shield,
  User,
  Pencil,
} from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-admin-staff',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, LucideAngularModule, TranslatePipe],
  templateUrl: './staff.component.html',
})
export class AdminStaffComponent implements OnInit {
  private adminService = inject(AdminService);
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  staff = signal<StaffDto[]>([]);
  loading = signal(true);

  showCreateDialog = signal(false);
  showEditDialog = signal(false);
  selectedStaff = signal<StaffDto | null>(null);
  submitting = signal(false);
  errorMsg = signal('');

  UserPlus = UserPlus;
  Search = Search;
  RefreshCw = RefreshCw;
  X = X;
  Shield = Shield;
  User = User;
  Pencil = Pencil;

  createForm = this.fb.group({
    fullName: ['', [Validators.required, Validators.minLength(2)]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^\+?[0-9]{9,13}$/)]],
    password: ['', [Validators.required, Validators.minLength(6)]],
    gender: [0, Validators.required],
    role: [2, Validators.required], // 2 = Operator, 5 = Admin
  });

  editForm = this.fb.group({
    fullName: ['', [Validators.required, Validators.minLength(2)]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^\+?[0-9]{9,13}$/)]],
    password: ['', Validators.minLength(6)],
    gender: [0, Validators.required],
    role: [2, Validators.required],
  });

  ngOnInit() {
    this.loadStaff();
  }

  loadStaff() {
    this.loading.set(true);
    this.adminService
      .getStaff()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.staff.set(res.data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  openCreate() {
    this.createForm.reset({ gender: 0, role: 2 });
    this.errorMsg.set('');
    this.showCreateDialog.set(true);
  }

  submitCreate() {
    if (this.createForm.invalid) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.createForm.value;
    this.adminService
      .createStaff({
        fullName: v.fullName!,
        phoneNumber: v.phoneNumber!,
        password: v.password!,
        gender: v.gender!,
        role: Number(v.role),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success) {
            this.showCreateDialog.set(false);
            this.loadStaff();
          } else {
            this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
          }
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik yuz berdi');
        },
      });
  }

  openEdit(staff: StaffDto) {
    this.selectedStaff.set(staff);
    this.editForm.reset({
      fullName: staff.fullName,
      phoneNumber: staff.phoneNumber,
      password: '',
      gender: staff.gender,
      role: this.roleValue(staff.role),
    });
    this.errorMsg.set('');
    this.showEditDialog.set(true);
  }

  submitEdit() {
    if (this.editForm.invalid || !this.selectedStaff()) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.editForm.value;
    this.adminService
      .updateStaff(this.selectedStaff()!.id, {
        fullName: v.fullName!,
        phoneNumber: v.phoneNumber!,
        gender: Number(v.gender),
        role: Number(v.role),
        password: v.password || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success !== false) {
            this.showEditDialog.set(false);
            this.loadStaff();
          } else {
            this.errorMsg.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
          }
        },
        error: (err) => {
          this.submitting.set(false);
          this.errorMsg.set(err?.error?.message || 'Xatolik yuz berdi');
        },
      });
  }

  roleLabel(role: string): string {
    if (role === 'Admin') return 'Admin';
    if (role === 'Operator') return 'Operator';
    return role;
  }

  roleClass(role: string): string {
    if (role === 'Admin')
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  }

  roleValue(role: string): number {
    return role === 'Admin' ? 5 : 2;
  }

  formatDate(d: string): string {
    return new Date(d).toLocaleDateString('uz-UZ');
  }
}
